import { useMutation, useQueryClient } from "@tanstack/react-query";
import { adminRaffleKeys } from "Api/Queries/Raffle/useAdminRaffleRoundsQuery";
import {
  type ApiProblemDetails,
  type ErrorResponse,
  type FetchResponse,
  postAdminRaffleRoundsPublicIdAnnounce,
  postAdminRaffleRoundsPublicIdCancel,
  postAdminRaffleRoundsPublicIdClose,
  postAdminRaffleRoundsPublicIdComplete,
  postAdminRaffleRoundsPublicIdSetDefault,
  type RaffleRoundDto,
} from "Infrastructure/Api/Api";
import { processResponseWithApiProblemDetails } from "Infrastructure/Api/ApiUtils";

type Props = {
  onSuccess?: (result: RaffleRoundDto) => void;
  onError?: () => void;
};

// The updated round or a problem (400 only from Announce).
type RoundLifecycleResponse =
  | FetchResponse<RaffleRoundDto, 200>
  | FetchResponse<ApiProblemDetails, 400>
  | FetchResponse<ApiProblemDetails, 404>
  | FetchResponse<ApiProblemDetails, 409>
  | ErrorResponse;

type RoundLifecycleCall = (publicID: string) => Promise<RoundLifecycleResponse>;

export type RaffleRoundLifecycleAction =
  | "announce"
  | "close"
  | "complete"
  | "cancel"
  | "setDefault";

const ADMIN_RAFFLE_LIFECYCLE_MUTATION_KEY = "ADMIN_RAFFLE_LIFECYCLE";

// Keyed (variables = publicID) so the rounds page can find in-flight rounds via useMutationState.
export const raffleRoundLifecycleKeys = {
  all: [ADMIN_RAFFLE_LIFECYCLE_MUTATION_KEY],
  action: (action: RaffleRoundLifecycleAction) => [
    ...raffleRoundLifecycleKeys.all,
    action,
  ],
};

const useRoundLifecycleMutation = (
  action: RaffleRoundLifecycleAction,
  apiCall: RoundLifecycleCall,
  { onSuccess, onError }: Props,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: raffleRoundLifecycleKeys.action(action),
    mutationFn: processResponseWithApiProblemDetails((publicID: string) =>
      apiCall(publicID),
    ),
    onSuccess,
    onError,
    // Refresh on failure too (a 409 means the list is outdated). Returned, so the row stays locked
    // until the refetch lands instead of briefly re-enabling on the stale status.
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: adminRaffleKeys.all }),
  });
};

// Draft → Announced: the rewards and draw date become the locked public promise.
export const useAnnounceRaffleRoundMutation = (props: Props = {}) =>
  useRoundLifecycleMutation(
    "announce",
    postAdminRaffleRoundsPublicIdAnnounce,
    props,
  );

// Announced → Closed (irreversible): numbers the tickets and seals the export.
export const useCloseRaffleRoundMutation = (props: Props = {}) =>
  useRoundLifecycleMutation("close", postAdminRaffleRoundsPublicIdClose, props);

// Closed → Drawn: records that the offline draw took place.
export const useCompleteRaffleRoundMutation = (props: Props = {}) =>
  useRoundLifecycleMutation(
    "complete",
    postAdminRaffleRoundsPublicIdComplete,
    props,
  );

// Draft extra competition → Cancelled (kept on record); never an announced one (R7-Q5).
export const useCancelRaffleRoundMutation = (props: Props = {}) =>
  useRoundLifecycleMutation(
    "cancel",
    postAdminRaffleRoundsPublicIdCancel,
    props,
  );

// Makes another open round the market's main round. The old main keeps its tickets if announced;
// a draft hands them over.
export const useSetDefaultRaffleRoundMutation = (props: Props = {}) =>
  useRoundLifecycleMutation(
    "setDefault",
    postAdminRaffleRoundsPublicIdSetDefault,
    props,
  );
